import React, { useState, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { ContactInfo, ContactPhotoData, generateFallbackPhoto } from '../../hooks/useContactPhotos';
import { Building, User, RefreshCw } from 'lucide-react';

export interface SmartAvatarProps {
  contact: ContactInfo;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  showOnlineIndicator?: boolean;
  isOnline?: boolean;
  enablePhotoEnrichment?: boolean;
  onClick?: () => void;
  showCompanyLogo?: boolean;
  fallbackIcon?: 'user' | 'building';
}

const sizeClasses = {
  xs: 'h-6 w-6 text-xs',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
  '2xl': 'h-20 w-20 text-xl'
};

const indicatorSizes = {
  xs: 'h-1.5 w-1.5',
  sm: 'h-2 w-2',
  md: 'h-2.5 w-2.5',
  lg: 'h-3 w-3',
  xl: 'h-3.5 w-3.5',
  '2xl': 'h-4 w-4'
};

export const SmartAvatar: React.FC<SmartAvatarProps> = ({
  contact,
  size = 'md',
  className,
  showOnlineIndicator = false,
  isOnline = false,
  enablePhotoEnrichment = true,
  onClick,
  showCompanyLogo = false,
  fallbackIcon = 'user'
}) => {
  const [photoData, setPhotoData] = useState<ContactPhotoData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Generate fallback data immediately
  const fallbackData = generateFallbackPhoto(contact);

  useEffect(() => {
    if (enablePhotoEnrichment && contact.email) {
      enrichPhoto();
    }
  }, [contact.email, enablePhotoEnrichment]);

  const enrichPhoto = async () => {
    if (!contact.email || isLoading) return;

    setIsLoading(true);
    try {
      const response = await fetch('/api/contacts/photos/single', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(contact)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setPhotoData(result.photoData);
        }
      }
    } catch (error) {
      console.debug('Photo enrichment failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageError(false);
  };

  // Determine what to display
  const displayData = photoData || fallbackData;
  const hasPhoto = displayData.photoUrl && !imageError;
  const showCompanyBackground = showCompanyLogo && displayData.companyLogoUrl;

  const getInitials = () => {
    return displayData.fallbackInitials || fallbackData.fallbackInitials;
  };

  const getBackgroundColor = () => {
    return displayData.fallbackColor || fallbackData.fallbackColor;
  };

  const avatarContent = () => {
    if (hasPhoto) {
      return (
        <img
          src={displayData.photoUrl}
          alt={contact.name || contact.email}
          className="rounded-full object-cover w-full h-full"
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
      );
    }

    // Show company logo as background if available
    if (showCompanyBackground) {
      return (
        <div className="relative w-full h-full">
          <img
            src={displayData.companyLogoUrl}
            alt={contact.company || ''}
            className="absolute inset-0 w-full h-full object-cover opacity-20 rounded-full"
          />
          <div className="relative z-10 flex items-center justify-center h-full w-full">
            <span className="font-semibold text-white drop-shadow-sm">
              {getInitials()}
            </span>
          </div>
        </div>
      );
    }

    // Fallback to initials or icon
    if (getInitials() && getInitials() !== 'U') {
      return (
        <span className="font-semibold text-white">
          {getInitials()}
        </span>
      );
    }

    // Ultimate fallback - icon
    const IconComponent = fallbackIcon === 'building' ? Building : User;
    return <IconComponent className="w-1/2 h-1/2 text-white" />;
  };

  return (
    <div className="relative">
      <div
        className={cn(
          'rounded-full bg-gradient-to-br from-blue-100 to-purple-100 text-blue-700 font-semibold flex items-center justify-center overflow-hidden relative',
          'dark:from-blue-900 dark:to-purple-900 dark:text-blue-300',
          sizeClasses[size],
          onClick && 'cursor-pointer hover:opacity-80 transition-opacity',
          className
        )}
        style={{
          backgroundColor: !hasPhoto ? getBackgroundColor() : undefined
        }}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        {avatarContent()}

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center rounded-full">
            <RefreshCw className="w-1/3 h-1/3 text-white animate-spin" />
          </div>
        )}
      </div>

      {/* Online indicator */}
      {showOnlineIndicator && (
        <div
          className={cn(
            "absolute -bottom-0.5 -right-0.5 rounded-full border-2 border-white dark:border-gray-900",
            indicatorSizes[size],
            isOnline ? "bg-green-500" : "bg-gray-400"
          )}
        />
      )}

      {/* Photo source indicator (for debugging/dev) */}
      {process.env.NODE_ENV === 'development' && photoData && (
        <div className="absolute -top-1 -right-1 bg-black/70 text-white text-xs px-1 rounded text-[8px] leading-none">
          {photoData.source}
        </div>
      )}
    </div>
  );
};

/**
 * Group Avatar Component for displaying multiple contacts
 */
export interface GroupAvatarProps {
  contacts: ContactInfo[];
  size?: SmartAvatarProps['size'];
  maxVisible?: number;
  className?: string;
  enablePhotoEnrichment?: boolean;
  onGroupClick?: () => void;
  spacing?: 'tight' | 'normal' | 'loose';
}

export const GroupAvatar: React.FC<GroupAvatarProps> = ({
  contacts,
  size = 'md',
  maxVisible = 3,
  className,
  enablePhotoEnrichment = true,
  onGroupClick,
  spacing = 'normal'
}) => {
  const visibleContacts = contacts.slice(0, maxVisible);
  const remainingCount = Math.max(0, contacts.length - maxVisible);

  const spacingClasses = {
    tight: '-ml-1',
    normal: '-ml-2',
    loose: '-ml-1'
  };

  return (
    <div 
      className={cn(
        'flex items-center',
        onGroupClick && 'cursor-pointer',
        className
      )}
      onClick={onGroupClick}
    >
      {visibleContacts.map((contact, index) => (
        <div
          key={contact.email}
          className={cn(
            'relative ring-2 ring-white dark:ring-gray-900 rounded-full',
            index > 0 && spacingClasses[spacing]
          )}
          style={{ zIndex: visibleContacts.length - index }}
        >
          <SmartAvatar
            contact={contact}
            size={size}
            enablePhotoEnrichment={enablePhotoEnrichment}
          />
        </div>
      ))}

      {remainingCount > 0 && (
        <div
          className={cn(
            'rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-semibold flex items-center justify-center ring-2 ring-white dark:ring-gray-900',
            sizeClasses[size],
            spacingClasses[spacing]
          )}
          style={{ zIndex: 0 }}
        >
          <span className="text-xs">+{remainingCount}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Company Avatar Component - focuses on company branding
 */
export interface CompanyAvatarProps {
  company: string;
  domain?: string;
  contacts?: ContactInfo[];
  size?: SmartAvatarProps['size'];
  className?: string;
  onClick?: () => void;
}

export const CompanyAvatar: React.FC<CompanyAvatarProps> = ({
  company,
  domain,
  contacts = [],
  size = 'md',
  className,
  onClick
}) => {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    if (domain) {
      // Try Clearbit logo first
      const clearbitUrl = `https://logo.clearbit.com/${domain}`;
      
      // Test if logo exists
      const img = new Image();
      img.onload = () => setLogoUrl(clearbitUrl);
      img.onerror = () => {
        // Fallback to favicon
        setLogoUrl(`https://www.google.com/s2/favicons?sz=64&domain=${domain}`);
      };
      img.src = clearbitUrl;
    }
  }, [domain]);

  const companyInitials = company
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <div className="relative">
      <div
        className={cn(
          'rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 text-gray-700 dark:text-gray-300 font-semibold flex items-center justify-center overflow-hidden',
          sizeClasses[size],
          onClick && 'cursor-pointer hover:opacity-80 transition-opacity',
          className
        )}
        onClick={onClick}
      >
        {logoUrl && !logoError ? (
          <img
            src={logoUrl}
            alt={company}
            className="w-full h-full object-contain p-1"
            onError={() => setLogoError(true)}
          />
        ) : (
          <span className="font-semibold">{companyInitials}</span>
        )}
      </div>

      {/* Contact count indicator */}
      {contacts.length > 0 && (
        <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
          {contacts.length}
        </div>
      )}
    </div>
  );
}; 